import type {
  IAxisZoomBounds,
  IChartZoomState,
  IViewportZoomBounds,
} from "../../../../../../../components/common/chart-plugins";

export interface IChartOverviewDomain {
  x?: IAxisZoomBounds;
  y: Record<string, IAxisZoomBounds>;
  reverseX?: boolean;
  reverseY?: Record<string, boolean>;
  cascade?: {
    xOffset: number;
    yOffset: number;
    datasets: Array<Array<{ x: number }>>;
  };
}

const FULL_VIEWPORT: IViewportZoomBounds = { left: 0, right: 1, top: 0, bottom: 1 };
const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));
const isValidBounds = (bounds: IAxisZoomBounds | undefined): bounds is IAxisZoomBounds =>
  Boolean(bounds && Number.isFinite(bounds.min) && Number.isFinite(bounds.max) && bounds.min < bounds.max);
const shiftBounds = (bounds: IAxisZoomBounds, delta: number): IAxisZoomBounds => ({
  min: bounds.min + delta,
  max: bounds.max + delta,
});

const projectBounds = (
  bounds: IAxisZoomBounds | undefined,
  domain: IAxisZoomBounds | undefined,
  reversed: boolean,
): { start: number; end: number } => {
  if (!isValidBounds(bounds) || !isValidBounds(domain)) {
    return { start: 0, end: 1 };
  }
  const span = domain.max - domain.min;
  const start = clamp((bounds.min - domain.min) / span, 0, 1);
  const end = clamp((bounds.max - domain.min) / span, 0, 1);
  return reversed ? { start: 1 - end, end: 1 - start } : { start, end };
};

const getCascadeXBounds = (zoomState: IChartZoomState, domain: IChartOverviewDomain): IAxisZoomBounds | undefined => {
  if (zoomState.selectionX) {
    return zoomState.selectionX;
  }
  const range = zoomState.itemRange;
  const items = range && domain.cascade?.datasets[range.datasetIndex];
  if (!range || !items) {
    return undefined;
  }
  const first = items[range.startIndex]?.x;
  const last = items[range.endIndex]?.x;
  return Number.isFinite(first) && Number.isFinite(last)
    ? { min: Math.min(first, last), max: Math.max(first, last) }
    : undefined;
};

export const getChartOverviewViewport = (
  zoomState: IChartZoomState | null,
  domain: IChartOverviewDomain,
): IViewportZoomBounds => {
  if (!zoomState) {
    return { ...FULL_VIEWPORT };
  }
  if (zoomState.viewport) {
    return { ...zoomState.viewport };
  }

  const datasetIndex = zoomState.baseDatasetIndex ?? zoomState.itemRange?.datasetIndex ?? 0;
  const cascadeX = domain.cascade && getCascadeXBounds(zoomState, domain);
  const xBounds = cascadeX ? shiftBounds(cascadeX, datasetIndex * (domain.cascade?.xOffset ?? 0)) : zoomState.x;
  const yScaleId = Object.keys(zoomState.y ?? {}).find(id => domain.y[id]) ?? Object.keys(domain.y)[0];
  const yBounds =
    domain.cascade && zoomState.selectionY
      ? shiftBounds(zoomState.selectionY, datasetIndex * domain.cascade.yOffset)
      : zoomState.y?.[yScaleId];
  const x = projectBounds(xBounds, domain.x, domain.reverseX === true);
  const y = projectBounds(yBounds, domain.y[yScaleId], domain.reverseY?.[yScaleId] !== true);

  return { left: x.start, right: x.end, top: y.start, bottom: y.end };
};

const getAxisDelta = (
  bounds: IAxisZoomBounds,
  domain: IAxisZoomBounds | undefined,
  delta: number,
  reversed: boolean,
): number => {
  if (!isValidBounds(bounds) || !isValidBounds(domain) || !Number.isFinite(delta)) {
    return 0;
  }
  const span = domain.max - domain.min;
  if (bounds.max - bounds.min >= span) {
    return 0;
  }
  return clamp(delta * span * (reversed ? -1 : 1), domain.min - bounds.min, domain.max - bounds.max);
};

export const panChartOverview = (
  zoomState: IChartZoomState,
  domain: IChartOverviewDomain,
  deltaX: number,
  deltaY: number,
): IChartZoomState | null => {
  const viewport = getChartOverviewViewport(zoomState, domain);
  const dx = Number.isFinite(deltaX) ? clamp(deltaX, -viewport.left, 1 - viewport.right) : 0;
  let dy = Number.isFinite(deltaY) ? clamp(deltaY, -viewport.top, 1 - viewport.bottom) : 0;
  if (zoomState.viewport) {
    return {
      ...zoomState,
      viewport: {
        left: viewport.left + dx,
        right: viewport.right + dx,
        top: viewport.top + dy,
        bottom: viewport.bottom + dy,
      },
    };
  }

  const nextState = { ...zoomState };
  const cascade = domain.cascade;
  const cascadeX = cascade && getCascadeXBounds(zoomState, domain);
  if (cascade && cascadeX && zoomState.itemRange && isValidBounds(domain.x)) {
    const range = zoomState.itemRange;
    const items = cascade.datasets[range.datasetIndex];
    const sampleBounds = { min: Infinity, max: -Infinity };
    items?.forEach(item => {
      sampleBounds.min = Math.min(sampleBounds.min, item.x);
      sampleBounds.max = Math.max(sampleBounds.max, item.x);
    });
    const requestedDelta = dx * (domain.x.max - domain.x.min) * (domain.reverseX ? -1 : 1);
    const sampleSpan = sampleBounds.max - sampleBounds.min;
    const delta =
      isValidBounds(sampleBounds) && cascadeX.max - cascadeX.min < sampleSpan
        ? clamp(
            requestedDelta,
            Math.min(0, sampleBounds.min - cascadeX.min),
            Math.max(0, sampleBounds.max - cascadeX.max),
          )
        : 0;
    const bounds = shiftBounds(cascadeX, delta);
    let startIndex = -1;
    let endIndex = -1;
    items?.forEach((item, index) => {
      if (item.x >= bounds.min && item.x <= bounds.max) {
        if (startIndex === -1) startIndex = index;
        endIndex = index;
      }
    });
    // Let the caller keep the last displayed window when the gesture crosses a sample gap.
    if (startIndex === -1) return null;
    nextState.selectionX = bounds;
    nextState.itemRange = { ...range, startIndex, endIndex };
    if (zoomState.x) nextState.x = shiftBounds(zoomState.x, delta);
  } else if (zoomState.x) {
    nextState.x = shiftBounds(zoomState.x, getAxisDelta(zoomState.x, domain.x, dx, domain.reverseX === true));
  }

  if (!cascade || !zoomState.selectionY) {
    // Keep axes with different units aligned, even if their initial ranges differ.
    Object.entries(zoomState.y ?? {}).forEach(([id, bounds]) => {
      const axis = projectBounds(bounds, domain.y[id], domain.reverseY?.[id] !== true);
      dy = clamp(dy, -axis.start, 1 - axis.end);
    });
  }
  if (zoomState.y) {
    nextState.y = Object.fromEntries(
      Object.entries(zoomState.y).map(([id, bounds]) => {
        const original = domain.y[id];
        const delta = isValidBounds(original)
          ? dy * (original.max - original.min) * (domain.reverseY?.[id] ? 1 : -1)
          : 0;
        return [id, shiftBounds(bounds, delta)];
      }),
    );
  }
  if (cascade && zoomState.selectionY) {
    const id = Object.keys(zoomState.y ?? {}).find(key => domain.y[key]) ?? Object.keys(domain.y)[0];
    const original = domain.y[id];
    if (isValidBounds(original)) {
      nextState.selectionY = shiftBounds(
        zoomState.selectionY,
        dy * (original.max - original.min) * (domain.reverseY?.[id] ? 1 : -1),
      );
    }
  }
  return nextState;
};
